/**
 * Typed fetch wrapper.
 *
 * Requests are same-origin (Vite proxies /api in dev), so the session cookie
 * travels automatically and there is no token to manage in JavaScript.
 */
import type {
  ActivityChatsResponse,
  ActivityFeedResponse,
  ActivityHoursResponse,
  AiConceptsResponse,
  ConceptPerformanceResponse,
  ActivityKind,
  ActivityPeopleResponse,
  AdAccountConfig,
  ApiErrorCode,
  ApiKeyCreateRequest,
  ApiKeyCreateResponse,
  ApiKeyInfo,
  ApiKeyRevokeResponse,
  AppSettings,
  AuthConfig,
  EditorCreateRequest,
  StrategistConfig,
  StrategistCreateRequest,
  StrategistInfo,
  StrategistPatchRequest,
  BriefAssignRequest,
  BriefAssignResponse,
  BriefStrategistRequest,
  BriefStrategistResponse,
  BriefProductRequest,
  BriefProductResponse,
  BriefCategoryRequest,
  BriefCategoryResponse,
  ConceptProduct,
  BriefComment,
  BriefCommentEditRequest,
  BriefCommentHistoryResponse,
  BriefCommentReactionRequest,
  BriefCommentRequest,
  BriefCommentsResponse,
  BriefDetailResponse,
  BriefFile,
  BriefListResponse,
  ConceptPeriodSummary,
  BriefPatchRequest,
  BriefPatchResponse,
  BriefTransitionRequest,
  BriefTransitionResponse,
  PipelineLookupsResponse,
  Resource,
  ResourceCreateRequest,
  ResourcePatchRequest,
  ResourcesResponse,
  GoogleSignInRequest,
  PipelineLane,
  PipelineNotificationsReadRequest,
  PipelineNotificationsResponse,
  PipelineStage,
  CreativeDetailResponse,
  EditorConfig,
  EditorDetailResponse,
  EditorSaved,
  LeaderboardResponse,
  MySummaryResponse,
  NewWinnersResponse,
  PeriodSummary,
  PrizeConfig,
  PrizeConfigInput,
  PrizeContestsResponse,
  Role,
  SessionInfo,
  StrategistResponse,
  SyncRunInfo,
  TopVideosResponse,
  UserAccount,
  UserAccountPatch,
  ViewAsOptions,
  ViewAsRequest,
  StoryboardDocument,
  StoryboardEditResult,
  StoryboardLineCreateInput,
  StoryboardLineCreateResult,
  StoryboardLineDeleteInput,
  StoryboardLineOrderInput,
  StoryboardLineDelivery,
  StoryboardLinePatch,
  StoryboardPatchMeta,
  StoryboardRestoreInput,
  StoryboardRevision,
  StoryboardDiscardResult,
  StoryboardSaveInput,
  StoryboardSaveResult,
  StoryboardSaveState,
  StoryboardShotCreateInput,
  StoryboardShotCreateResult,
  StoryboardShotOrderInput,
  StoryboardShotPatch,
  StoryboardMediaMode,
  StoryboardSlotCreateInput,
  StoryboardSlotCreateResult,
  StoryboardSlotPatch,
  StoryboardSummary,
  StoryboardTake,
  StoryboardTakeFileKind,
  StoryboardUndoBlocker,
  StoryboardViewResponse,
  StoryboardPromptsResponse,
  UndoCallBlocker,
  GalleryHideInput,
  GalleryHideResult,
  GalleryResult,
  ConceptFramesResult,
  GenerationAdoptInput,
  GenerationAdoptVoiceInput,
  GenerationAdoptToSlotInput,
  GenerationBudget,
  GenerationCancelResult,
  GenerationCreateInput,
  GenerationCreateResult,
  GenerationErrorCode,
  GenerationListResult,
  GenerationOptionInput,
  GenerationRetryInput,
  GenerationSettingsPatch,
  GenerationSettingsView,
  GenerationSpend,
  GenerationSpendFeed,
  GenerationUnadoptedResult,
  GenerationUploadKind,
  GenerationUploadResult,
  GenerationVoice,
  LibraryAddInput,
  LibraryImage,
  GenerationPrefs,
  LibraryPatchInput,
  LineGalleryResult,
  ProductSop,
  StoryboardReferencesResult,
  VoiceAddInput,
  VoiceAddResult,
  VoiceResolveResult,
  VoiceLookupResult,
  VoiceCheckCode,
  VoicePatchInput,
  VoiceoverAAdoptInput,
  VoiceoverBAdoptInput,
  VoiceoverRebuildInput,
  RevoiceApplyInput,
  RevoiceApplyResult,
  RevoiceBatchView,
  RevoiceCreateInput,
  RevoiceCreateResult,
  RevoiceListResult,
  RevoiceRetryInput,
  WorkSessionDetail,
  WorkSessionsQuery,
  WorkSessionsResponse,
  ErrorCode as StudioErrorCode,
} from "@scandi/shared";
import { surfaceHeaders } from "@/lib/surface";

/**
 * Every code a failure can carry: the app's own (`ApiErrorCode`), the
 * generation routes' (`GenerationErrorCode`, the voice check's answers), the
 * studio's and the agents' admin API's (`ErrorCode`, CONTRACTS.md §19), the
 * undo route's two refusals, and the WhatsApp contact checks, which have no
 * other home.
 */
export type ClientErrorCode =
  | ApiErrorCode
  | GenerationErrorCode
  | VoiceCheckCode
  | StudioErrorCode
  | "not_yours"
  | "not_undoable"
  | "save_moved"
  | "clips_present"
  | "images_only"
  /** The prompt chat's card moved on since it was read: replaced, withdrawn or already submitting. */
  | "card_changed"
  | "card_not_ready"
  /** The WhatsApp contact field: a bad number, a bad username, or both at once. */
  | "invalid_phone"
  | "invalid_wa_username"
  | "invalid_wa_contact";

/**
 * A failed request.
 *
 * `message` is the server's own English prose and is the fallback for anything
 * unexpected. `code` is what makes a failure translatable: the server names the
 * ones a user is meant to read, and the UI looks the name up in its catalog.
 * Without it every error message would arrive in English regardless of language.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ClientErrorCode | null;
  /** Seconds until a rate-limited caller may try again, when the server says. */
  readonly retryAfter: number | null;
  /**
   * Whether `message` came from the server or was invented here.
   *
   * The UI shows the server's own words for anything it has no code for, which
   * is better than an apology - but only when there are words. When the response
   * carried no message at all, `message` is a placeholder for the console and
   * the UI says something translated instead.
   */
  readonly fromServer: boolean;
  /**
   * On a storyboard's `409 conflict`: the revision the storyboard is at now.
   *
   * A storyboard write carries the revision the page last read, and the
   * server refuses one built from an older read rather than overwrite a newer
   * change. The refusal names the current number so the page can reload and
   * re-base without parsing a sentence. Null on every other failure.
   */
  readonly revision: number | null;
  /** The storyboard service's "what to do instead", when it has one. English, for diagnostics. */
  readonly hint: string | null;
  /**
   * On a 409 `undo_conflict`: the later changes in the way, newest first - a
   * storyboard's (`StoryboardUndoBlocker`) or a voice's or library image's
   * (`UndoCallBlocker`). Empty on every other failure.
   */
  readonly blockers: Array<StoryboardUndoBlocker | UndoCallBlocker>;
  /**
   * Everything else the refusal carried beside its words: a `cap_reached`'s
   * cap and spend, a `duplicate` voice's row. Read through the narrow helpers
   * that know each shape, never shown raw.
   */
  readonly details: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code: ClientErrorCode | null = null,
    retryAfter: number | null = null,
    fromServer = true,
    extra: {
      revision?: number | null;
      hint?: string | null;
      blockers?: Array<StoryboardUndoBlocker | UndoCallBlocker>;
      details?: Record<string, unknown>;
    } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
    this.fromServer = fromServer;
    this.revision = extra.revision ?? null;
    this.hint = extra.hint ?? null;
    this.blockers = extra.blockers ?? [];
    this.details = extra.details ?? {};
  }
}

/** An undo or redo refused because somebody changed the same thing since (`blockers` says who). */
export const isUndoConflict = (error: unknown): error is ApiError =>
  error instanceof ApiError && error.code === "undo_conflict";

/**
 * A storyboard write refused because somebody else saved first.
 *
 * Narrower than "any 409": a slot key that is already taken is a conflict
 * too, but it carries no revision and reloading would not fix it.
 */
export const isRevisionConflict = (error: unknown): error is ApiError & { revision: number } =>
  error instanceof ApiError && error.code === "conflict" && error.revision !== null;

export const isUnauthorized = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401;

/**
 * A failure that means the session itself is no longer what the app thinks it
 * is: signed out elsewhere, disabled, demoted back to no role, or demoted to a
 * role without admin or strategist rights. Any of these is answered by
 * re-reading the session, not by showing the error - that is what takes the
 * admin menus away from someone who is no longer an admin.
 *
 * No loop: only the session query is re-read, and a failure of the session
 * query itself never triggers this (see main.tsx). If the session still says
 * admin, nothing else refetches, so the refusal is simply shown.
 */
export const isSessionFailure = (error: unknown): boolean =>
  error instanceof ApiError &&
  (error.status === 401 ||
    error.code === "no_access" ||
    error.code === "account_disabled" ||
    error.code === "admin_required" ||
    error.code === "strategist_required" ||
    error.code === "pipeline_required");

/**
 * Exported so features with their own endpoints (the AI coach) decode failures
 * exactly the way the rest of the app does, rather than growing a second,
 * subtly different idea of what a failed response looks like.
 */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      // JSON unless the caller is uploading: a `FormData` body has to keep the
      // browser's own multipart boundary, and declaring a type by hand is the
      // classic way to make the server read an empty upload.
      ...(init.body && !(init.body instanceof FormData)
        ? { "content-type": "application/json" }
        : {}),
      // The change ledger: which surface and tab a write comes from (`lib/surface.ts`).
      ...(init.method && init.method !== "GET" && init.method !== "HEAD" ? surfaceHeaders() : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      error?: string;
      code?: ClientErrorCode;
      retryAfter?: number;
      hint?: string;
      revision?: number;
      blockers?: unknown;
      [extra: string]: unknown;
    } | null;
    const { error: _words, code: _code, retryAfter: _retry, hint: _hint, revision: _rev, blockers, ...details } = body ?? {};
    throw new ApiError(
      body?.error ?? `Request failed (${response.status})`,
      response.status,
      body?.code ?? null,
      body?.retryAfter ?? null,
      body?.error !== undefined,
      {
        revision: typeof body?.revision === "number" ? body.revision : null,
        hint: typeof body?.hint === "string" ? body.hint : null,
        blockers: Array.isArray(blockers) ? (blockers as Array<StoryboardUndoBlocker | UndoCallBlocker>) : [],
        details,
      },
    );
  }
  // A delete may answer 204 with no body at all, which `json()` would reject.
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Range selection, mirrored by the API's own range parser. */
export type RangePreset =
  | "current_period"
  | "previous_period"
  | "last_7d"
  | "last_14d"
  | "last_30d"
  | "custom";

export interface RangeSelection {
  preset: RangePreset;
  /** Set when browsing a specific historical pay period. */
  periodIndex?: number;
  from?: string;
  to?: string;
}

export function rangeToParams(range: RangeSelection): string {
  const params = new URLSearchParams();
  if (range.periodIndex !== undefined) {
    params.set("periodIndex", String(range.periodIndex));
  } else {
    params.set("preset", range.preset);
    if (range.from) params.set("from", range.from);
    if (range.to) params.set("to", range.to);
  }
  return params.toString();
}

export const api = {
  /**
   * Whether Google sign-in is on for this instance, and the public Firebase keys
   * to start it with. Read at runtime rather than baked in by Vite, so the same
   * build works against any project.
   */
  authConfig: () => request<AuthConfig>("/api/auth/config"),

  session: () => request<SessionInfo>("/api/auth/session"),

  /**
   * Trades a Firebase ID token for our own session cookie. The token is used
   * once and thrown away: from here on the cookie is the session, exactly as it
   * is for a password login.
   */
  googleSignIn: (idToken: string) =>
    request<SessionInfo>("/api/auth/google", {
      method: "POST",
      body: JSON.stringify({ idToken } satisfies GoogleSignInRequest),
    }),

  login: (password: string) =>
    request<SessionInfo>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ password }),
    }),

  logout: () => request<SessionInfo>("/api/auth/logout", { method: "POST" }),

  /** Ends every session of the signed-in account, this one included. Accounts only. */
  logoutEverywhere: () =>
    request<SessionInfo>("/api/auth/logout-everywhere", { method: "POST" }),

  /** Who a real admin can view the platform as. Refused for everyone else. */
  viewAsOptions: () => request<ViewAsOptions>("/api/auth/view-as/options"),

  /** Start viewing as someone (or switch); answers with the session as they would see it. */
  startViewAs: (persona: ViewAsRequest) =>
    request<SessionInfo>("/api/auth/view-as", { method: "POST", body: JSON.stringify(persona) }),

  /** Back to the admin's own view. */
  stopViewAs: () => request<SessionInfo>("/api/auth/view-as", { method: "DELETE" }),

  leaderboard: (range: RangeSelection) =>
    request<LeaderboardResponse>(`/api/leaderboard?${rangeToParams(range)}`),

  topVideos: (range: RangeSelection, limit: number) =>
    request<TopVideosResponse>(`/api/videos?${rangeToParams(range)}&limit=${limit}`),

  /** Takes no range on purpose: always the last week. */
  newWinners: () => request<NewWinnersResponse>("/api/new-winners"),

  /** The signed-in editor's current pay period. Whose it is comes from the session. */
  mySummary: () => request<MySummaryResponse>("/api/me/summary"),

  /** The concepts made through the platform, for the "AI" tag on the rankings. */
  aiConcepts: () => request<AiConceptsResponse>("/api/ai-concepts"),

  /** Takes no range either: the contest window is fixed. */
  prizeContests: () => request<PrizeContestsResponse>("/api/prize-contests"),

  /**
   * Takes a pay period rather than a range: the fee is a per-period ceiling, so
   * "last 7 days" is not a question it can answer. Omit for the one in progress.
   */
  /** The strategist roster: names, tags, who is on the bonus board. */
  strategists: () => request<StrategistInfo[]>("/api/strategists"),

  strategist: (periodIndex?: number) =>
    request<StrategistResponse>(
      periodIndex === undefined ? "/api/strategist" : `/api/strategist?periodIndex=${periodIndex}`,
    ),

  periods: () =>
    request<{ periods: PeriodSummary[]; currentIndex: number; today: string }>("/api/periods"),

  editorDetail: (id: number, range: RangeSelection) =>
    request<EditorDetailResponse>(`/api/editors/${id}?${rangeToParams(range)}`),

  creativeDetail: (slug: string, range: RangeSelection) =>
    request<CreativeDetailResponse>(
      `/api/creatives/${encodeURIComponent(slug)}?${rangeToParams(range)}`,
    ),

  admin: {
    settings: () => request<AppSettings>("/api/admin/settings"),

    activity: {
      people: () => request<ActivityPeopleResponse>("/api/admin/activity/people"),

      feed: (params: ActivityFeedParams) => {
        const query = new URLSearchParams();
        if (params.user !== undefined) query.set("user", String(params.user));
        if (params.kinds && params.kinds.length > 0) query.set("kind", params.kinds.join(","));
        if (params.cursor !== undefined) query.set("cursor", String(params.cursor));
        if (params.limit !== undefined) query.set("limit", String(params.limit));
        if (params.since !== undefined) query.set("since", params.since);
        const qs = query.toString();
        return request<ActivityFeedResponse>(`/api/admin/activity/feed${qs ? `?${qs}` : ""}`);
      },

      hours: (params: ActivityPeriodParams) => request<ActivityHoursResponse>(`/api/admin/activity/hours?${periodQuery(params)}`),

      chats: (params: ActivityPeriodParams) => request<ActivityChatsResponse>(`/api/admin/activity/chats?${periodQuery(params)}`),

      // The change ledger's work sessions (docs/activity-ledger/01-plan.md).
      work: (params: WorkSessionsQuery) => {
        const query = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") query.set(k, String(v));
        const qs = query.toString();
        return request<WorkSessionsResponse>(`/api/admin/activity/work${qs ? `?${qs}` : ""}`);
      },
      workDetail: (id: string) => request<WorkSessionDetail>(`/api/admin/activity/work/${encodeURIComponent(id)}`),
    },

    updateSettings: (patch: Partial<AppSettings>) =>
      request<AppSettings>("/api/admin/settings", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),

    prizes: () => request<PrizeConfig[]>("/api/admin/prizes"),
    createPrize: (input: PrizeConfigInput) =>
      request<PrizeConfig>("/api/admin/prizes", { method: "POST", body: JSON.stringify(input) }),
    updatePrize: (id: number, patch: Partial<PrizeConfigInput>) =>
      request<PrizeConfig>(`/api/admin/prizes/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    deletePrize: (id: number) => request<{ ok: true }>(`/api/admin/prizes/${id}`, { method: "DELETE" }),
    editors: () => request<EditorConfig[]>("/api/admin/editors"),

    /** A name is enough: tags, colour and place on the board are suggested server side. */
    createEditor: (editor: EditorCreateRequest) =>
      request<EditorConfig>("/api/admin/editors", {
        method: "POST",
        body: JSON.stringify(editor),
      }),

    strategists: () => request<StrategistConfig[]>("/api/admin/strategists"),

    createStrategist: (strategist: StrategistCreateRequest) =>
      request<StrategistConfig>("/api/admin/strategists", {
        method: "POST",
        body: JSON.stringify(strategist),
      }),

    updateStrategist: (id: string, patch: StrategistPatchRequest) =>
      request<StrategistConfig>(`/api/admin/strategists/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),

    updateEditor: (id: number, patch: Partial<Omit<EditorConfig, "id">>) =>
      request<EditorSaved>(`/api/admin/editors/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),

    accounts: () => request<AdAccountConfig[]>("/api/admin/accounts"),

    setAccountEnabled: (id: string, enabled: boolean) =>
      request<AdAccountConfig>(`/api/admin/accounts/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled }),
      }),

    syncStatus: () =>
      request<{
        latest: SyncRunInfo | null;
        history: SyncRunInfo[];
        isRunning: boolean;
        breakerOpenUntil: string | null;
        consecutiveFailures: number;
      }>("/api/admin/sync"),

    runSync: (deep: boolean) =>
      request<{ status: string; reason?: string; rowsUpserted?: number }>("/api/admin/sync", {
        method: "POST",
        body: JSON.stringify({ deep }),
      }),

    trackerStatus: () =>
      request<{
        total: number;
        linkable: number;
        withFile: number;
        tested: number;
        lastSyncedAt: string | null;
      }>("/api/admin/tracker"),

    syncTracker: () =>
      request<{ status: string; reason?: string; rowsUpserted?: number; linkable?: number }>(
        "/api/admin/tracker/sync",
        { method: "POST" },
      ),

    revokeAllSessions: () =>
      request<{ revoked: number }>("/api/admin/sessions/revoke-all", { method: "POST" }),

    /**
     * AI generation's admin side (`/api/admin/generation`): the team's monthly
     * cap and settings, the Spend page's totals and feed, and "Check again"
     * on a clip we lost track of. Strategists and editors are refused (403).
     */
    generation: {
      settings: () => request<GenerationSettingsView>("/api/admin/generation/settings"),

      updateSettings: (patch: GenerationSettingsPatch) =>
        request<GenerationSettingsView>("/api/admin/generation/settings", { method: "PATCH", body: JSON.stringify(patch) }),

      /** Totals for a period (Europe/Rome dates, `YYYY-MM-DD`), by person, model and kind, with the cap's bar. */
      spend: (params: SpendParams = {}) =>
        request<GenerationSpend & { from: string; to: string; bucket: string }>(`/api/admin/generation/spend${queryOf(params)}`),

      /** What was generated, newest first, 50 a page; `cursor` is the previous page's `next`. */
      spendFeed: (params: SpendFeedParams = {}) =>
        request<GenerationSpendFeed>(`/api/admin/generation/spend/outputs${queryOf(params)}`),

      /** "Check again": the worker re-reads an `unknown` clip's status at Higgsfield. Costs nothing. */
      check: (outputId: number) =>
        request<{ requested: true; outputId: number }>(`/api/admin/generation/outputs/${outputId}/check`, { method: "POST" }),
    },

    /** Personal accounts: who has signed in, and what each of them may see. */
    users: {
      /** Accounts still waiting for a role come first. */
      list: () => request<UserAccount[]>("/api/admin/users"),

      update: (id: number, patch: UserAccountPatch) =>
        request<UserAccount>(`/api/admin/users/${id}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),

      /**
       * Deletes the account and signs it out everywhere. Nothing stops the same
       * person signing in again: they come back as a new account with no role.
       */
      remove: (id: number) =>
        request<unknown>(`/api/admin/users/${id}`, { method: "DELETE" }),
    },

    /**
     * Machine keys: what an agent authenticates with.
     *
     * `create` is the only response in the whole client that carries key
     * material, and it carries it once - the server stores a hash, so there is
     * no endpoint that could hand it back. It is never written to storage and
     * never logged here either.
     */
    apiKeys: {
      list: () => request<ApiKeyInfo[]>("/api/admin/api-keys"),

      create: (body: ApiKeyCreateRequest) =>
        request<ApiKeyCreateResponse>("/api/admin/api-keys", {
          method: "POST",
          body: JSON.stringify(body),
        }),

      /** Revoke, not delete: the row stays so the request log keeps its owner. */
      revoke: (id: number) =>
        request<ApiKeyRevokeResponse>(`/api/admin/api-keys/${id}/revoke`, { method: "POST" }),
    },
  },

  /**
   * The creative pipeline.
   *
   * Every one of these is scoped by the server from the session - an editor
   * asking for `scope: "all"` still gets only their own work - so the client
   * never sends a viewer, only a lens.
   */
  pipeline: {
    list: (params: BriefListParams = {}) =>
      request<BriefListResponse>(`/api/pipeline/briefs${queryOf(params)}`),

    /** By `ref` (`CPT-2139`), which is what the URL carries and what people say out loud. */
    get: (ref: string) =>
      request<BriefDetailResponse>(`/api/pipeline/briefs/${encodeURIComponent(ref)}`),

    /** The concept on Meta: its whole run, summed and per hook variant. */
    performance: (id: number) => request<ConceptPerformanceResponse>(`/api/pipeline/briefs/${id}/performance`),

    /** One pay period of concepts, counted for strategists and admins. */
    periodSummary: (periodIndex: number) =>
      request<ConceptPeriodSummary>(`/api/pipeline/period-summary?periodIndex=${periodIndex}`),

    notifications: (params: { limit?: number; unread?: boolean } = {}) =>
      request<PipelineNotificationsResponse>(
        `/api/pipeline/notifications${queryOf({ limit: params.limit, unread: params.unread ? "1" : undefined })}`,
      ),

    /** No ids means "everything of mine". */
    markRead: (ids?: number[]) =>
      request<{ marked: number }>("/api/pipeline/notifications/read", {
        method: "POST",
        body: JSON.stringify({ ids } satisfies PipelineNotificationsReadRequest),
      }),

    /**
     * One move. `rowVersion` is the version the caller last saw and travels as
     * `If-Match`: a brief that changed underneath them is refused with
     * `pipeline_conflict` rather than silently overwritten.
     */
    transition: (id: number, body: BriefTransitionRequest, rowVersion?: number | null) =>
      request<BriefTransitionResponse>(`/api/pipeline/briefs/${id}/transition`, {
        method: "POST",
        // Omitted rather than guessed when the caller never read a version: a
        // made-up `If-Match` would either refuse a legitimate move or, worse,
        // match by luck and defeat the check entirely.
        headers: rowVersion ? { "if-match": String(rowVersion) } : {},
        body: JSON.stringify(body),
      }),

    comment: (id: number, bodyMd: string, parentId?: number | null) =>
      request<BriefComment>(`/api/pipeline/briefs/${id}/comments`, {
        method: "POST",
        body: JSON.stringify({ bodyMd, ...(parentId ? { parentId } : {}) } satisfies BriefCommentRequest),
      }),

    /* ---------------------------------------------------------- brief chat */

    /**
     * The brief's chat, by the brief's numeric id (the chat routes take the
     * id, not the ref, like every write on a brief).
     *
     * Without `since`: the whole thread, oldest first. With it - the `now` the
     * previous answer handed back - only what changed since, plus the current
     * reactions of every live comment, which is what a poll merges by id.
     */
    comments: (id: number, since?: string | null) =>
      request<BriefCommentsResponse>(`/api/pipeline/briefs/${id}/comments${queryOf({ since: since ?? undefined })}`),

    /** The author rewrites their own message; the earlier body is kept and listed by `commentHistory`. */
    editComment: (id: number, commentId: number, bodyMd: string) =>
      request<BriefComment>(`/api/pipeline/briefs/${id}/comments/${commentId}`, {
        method: "PATCH",
        body: JSON.stringify({ bodyMd } satisfies BriefCommentEditRequest),
      }),

    /** Soft: the row stays, emptied, so replies keep their place. The author or an admin. Answers 204. */
    deleteComment: (id: number, commentId: number) =>
      request<void>(`/api/pipeline/briefs/${id}/comments/${commentId}`, { method: "DELETE" }),

    /** One emoji on (the default) or off again. Idempotent both ways; answers the comment with its reactions. */
    reactToComment: (id: number, commentId: number, emoji: string, on = true) =>
      request<BriefComment>(`/api/pipeline/briefs/${id}/comments/${commentId}/reactions`, {
        method: "POST",
        body: JSON.stringify({ emoji, on } satisfies BriefCommentReactionRequest),
      }),

    /** Every earlier body of an edited comment, oldest first. */
    commentHistory: (id: number, commentId: number) =>
      request<BriefCommentHistoryResponse>(`/api/pipeline/briefs/${id}/comments/${commentId}/history`),

    /* ------------------------------------------------------------ authoring */

    /** The pickers: products, lookups, editors with their workload. */
    lookups: () => request<PipelineLookupsResponse>("/api/pipeline/lookups"),

    /**
     * One save. `rowVersion` is what the caller last read and travels as
     * `If-Match`, so a brief somebody else edited meanwhile is refused with
     * `pipeline_conflict` rather than quietly overwritten.
     */
    patch: (id: number, body: BriefPatchRequest, rowVersion?: number | null) =>
      request<BriefPatchResponse>(`/api/pipeline/briefs/${id}`, {
        method: "PATCH",
        headers: rowVersion ? { "if-match": String(rowVersion) } : {},
        body: JSON.stringify(body),
      }),

    /**
     * Choose, change or remove a concept's editor: a strategist or an admin,
     * only before the editor starts (409 `pipeline_editor_locked` after).
     * Removing the editor of a concept that is ready for its editor takes it
     * back to a new presentation, which `stage` reports.
     */
    assign: (id: number, editorId: number | null) =>
      request<BriefAssignResponse>(`/api/pipeline/briefs/${id}/assign`, {
        method: "POST",
        body: JSON.stringify({ editorId } satisfies BriefAssignRequest),
      }),

    setStrategist: (id: number, strategistId: number | null) =>
      request<BriefStrategistResponse>(`/api/pipeline/briefs/${id}/strategist`, {
        method: "PUT",
        body: JSON.stringify({ strategistId } satisfies BriefStrategistRequest),
      }),

    /** What kind of concept it is: a category key, or null for none. Any strategist or admin. */
    setCategory: (id: number, category: string | null) =>
      request<BriefCategoryResponse>(`/api/pipeline/briefs/${id}/category`, {
        method: "PUT",
        body: JSON.stringify({ category } satisfies BriefCategoryRequest),
      }),

    /** What the ad sells. Any strategist or admin. */
    setProduct: (id: number, product: ConceptProduct) =>
      request<BriefProductResponse>(`/api/pipeline/briefs/${id}/product`, {
        method: "PUT",
        body: JSON.stringify({ product } satisfies BriefProductRequest),
      }),

    /**
     * A voice-over, as multipart.
     *
     * `FormData` rather than JSON because the alternative is base64, which
     * inflates an mp3 by a third for nothing. No `content-type` header is set:
     * the browser has to add its own multipart boundary, and setting one by
     * hand is the classic way to make the server see an empty body.
     */
    uploadFile: (id: number, file: File, label?: string | null, line?: string | null) => {
      const form = new FormData();
      form.set("file", file);
      if (label) form.set("label", label);
      // An editor's own audio for one line of the presentation (its key, `l03`): seen at once, no publish.
      if (line) form.set("line", line);
      return request<BriefFile>(`/api/pipeline/briefs/${id}/files`, { method: "POST", body: form });
    },

    removeFile: (id: number, fileId: number) =>
      request<void>(`/api/pipeline/briefs/${id}/files/${fileId}`, { method: "DELETE" }),
  },

  /**
   * Storyboards: the presentation as data (`STORYBOARDS.md`, "The portal API").
   *
   * `:id` is the storyboard's numeric id; the server also takes its slug, but
   * every page here reaches a storyboard through its concept
   * (`BriefDetail.storyboard.id`), so the id is what travels.
   *
   * Every write carries `baseRevision`, the revision the page last read: a
   * storyboard that moved on in between refuses the write with `409 conflict`
   * and its current revision (`ApiError.revision`) rather than overwrite a
   * newer change. The hooks in `use-storyboard.ts` hold that number; nothing
   * here guesses it. Each write answers `{ revision, changed }`; a create adds
   * the key the server minted.
   *
   * Who may do what is the server's: an editor reads only the published
   * snapshot of a released concept and may comment; edits are a strategist's
   * or an admin's.
   */
  storyboards: {
    /** Newest first. `brief` narrows to one concept (`CPT-3993`), `presentation` to one hosted deck's slug. */
    list: (params: StoryboardListParams = {}) =>
      request<{ storyboards: StoryboardSummary[] }>(
        `/api/pipeline/storyboards${queryOf({ ...params, archived: params.archived || undefined })}`,
      ),

    /**
     * The whole document with its timeline and notes. `published` reads the
     * editors' snapshot instead of the live draft (404 before the first
     * publish); `history` adds every take of every slot (the shot page's
     * question, too heavy for a summary); `archived` includes archived rows.
     */
    get: (id: number, params: StoryboardReadParams = {}) =>
      request<{ storyboard: StoryboardDocument }>(
        // Only the switches that are on: `?published=0` reads the same as no
        // switch at all, and a short URL is easier to read in a network log.
        `/api/pipeline/storyboards/${id}${queryOf({
          published: params.published || undefined,
          // The live document, said out loud: an editor working on their shots reads it only when
          // they ask (round 3, D1), and a strategist or an admin reads it either way.
          live: params.published ? undefined : true,
          history: params.history || undefined,
          archived: params.archived || undefined,
          // Without the prompts: half the bytes, and nothing a page draws (`prompts` below).
          lean: params.lean || undefined,
        })}`,
      ),

    /** What the deck draws, in one call: the published document, or the live one for a strategist before the first publish. */
    view: (id: number, params: { lean?: boolean } = {}) =>
      request<StoryboardViewResponse>(`/api/pipeline/storyboards/${id}/view${queryOf({ lean: params.lean || undefined })}`),

    /** The prompts a lean read left out, for the same read (`view: true` for the deck's). */
    prompts: (id: number, params: StoryboardReadParams & { view?: boolean } = {}) =>
      request<StoryboardPromptsResponse>(
        `/api/pipeline/storyboards/${id}/prompts${queryOf(
          params.view
            ? { view: true }
            : {
                published: params.published || undefined,
                live: params.published ? undefined : true,
                history: params.history || undefined,
                archived: params.archived || undefined,
              },
        )}`,
      ),

    /* ---- field edits */

    /** Title, brand, the concept it briefs, archive; `meta` is merged one top-level key at a time. */
    patch: (id: number, body: StoryboardWrite<StoryboardPatchRequest>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}`, "PATCH", body),

    /**
     * One line: words, speaker, delivery, a shot-less line's own length, and the strategist's note (`noteMd`, null clears it).
     * `keepalive` lets the request outlive the page: the script editor's save on the way out (`WriteOptions`).
     */
    patchLine: (id: number, key: string, body: StoryboardWrite<StoryboardLinePatchRequest>, options: WriteOptions = {}) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/lines/${encodeURIComponent(key)}`, "PATCH", body, options),

    /** One shot, or move it under another line with `{ line }`. `keepalive`: the line page's save on the way out. */
    patchShot: (id: number, key: string, body: StoryboardWrite<StoryboardShotPatchRequest>, options: WriteOptions = {}) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/shots/${encodeURIComponent(key)}`, "PATCH", body, options),

    /** A slot's label, prompt or params, or put an earlier take back on show (`currentTakeId`). */
    patchSlot: (id: number, slotId: number, body: StoryboardWrite<StoryboardSlotPatchRequest>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/slots/${slotId}`, "PATCH", body),

    /* ---- structure */

    /** A new line, after `after` or last; it starts with no shots and no voiceover. The server mints the key. */
    createLine: (id: number, body: StoryboardWrite<StoryboardLineCreateInput>) =>
      write<StoryboardLineCreateResult>(`/api/pipeline/storyboards/${id}/lines`, "POST", body),

    /** Archives the line and, by default, its shots and their slots; `restore` undoes it. */
    deleteLine: (id: number, key: string, body: StoryboardWrite<StoryboardLineDeleteInput> = {}) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/lines/${encodeURIComponent(key)}`, "DELETE", body),

    /** Every live line, each once, in the order wanted. */
    orderLines: (id: number, body: StoryboardWrite<StoryboardLineOrderInput>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/lines/order`, "POST", body),

    /** A new shot in a line, after `after` or last; it starts with no frames. The server mints the key. */
    createShot: (id: number, body: StoryboardWrite<StoryboardShotCreateInput>) =>
      write<StoryboardShotCreateResult>(`/api/pipeline/storyboards/${id}/shots`, "POST", body),

    /** Archives the shot and its slots. */
    deleteShot: (id: number, key: string, body: StoryboardWrite<object> = {}) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/shots/${encodeURIComponent(key)}`, "DELETE", body),

    /** Reorder within one line; moving to another line is `patchShot(..., { line })`. */
    orderShots: (id: number, body: StoryboardWrite<StoryboardShotOrderInput>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/shots/order`, "POST", body),

    /** A new frame option, a second voice. The key is minted when omitted; the answer carries the slot's id. */
    createSlot: (id: number, body: StoryboardWrite<StoryboardSlotCreateInput>) =>
      write<StoryboardSlotCreateResult>(`/api/pipeline/storyboards/${id}/slots`, "POST", body),

    /** Archives one slot. */
    deleteSlot: (id: number, slotId: number, body: StoryboardWrite<object> = {}) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/slots/${slotId}`, "DELETE", body),

    /** Undo an archive: a line or a shot by key, a slot by id. A line brings back the shots that went with it. */
    restore: (id: number, body: StoryboardWrite<StoryboardRestoreInput>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/restore`, "POST", body),

    /* ---- the save bar */

    /** The caller's unsaved changes, other people's, and the agent's since the last save. */
    saveState: (id: number) => request<StoryboardSaveState>(`/api/pipeline/storyboards/${id}/save-state`),

    /**
     * Save: the live document goes to the editors. A 409 `save_moved` (with
     * `details.saveState`) means somebody else wrote after `knownRevision`.
     */
    save: (id: number, body: StoryboardSaveInput) =>
      request<StoryboardSaveResult>(`/api/pipeline/storyboards/${id}/save`, { method: "POST", body: JSON.stringify(body) }),

    /** Discard: the caller's own unsaved changes reverted; `kept` names what stayed because somebody changed it since. */
    discard: (id: number) =>
      request<StoryboardDiscardResult>(`/api/pipeline/storyboards/${id}/discard`, { method: "POST", body: "{}" }),

    /* ---- history */

    /** Newest first. `key=s07` matches the shot and every slot under it; page with `before` = the answer's `nextBefore`. */
    revisions: (id: number, params: StoryboardRevisionParams = {}) =>
      request<{ revisions: StoryboardRevision[]; nextBefore: number | null }>(
        `/api/pipeline/storyboards/${id}/revisions${queryOf(params)}`,
      ),

    /* ---- media */

    /**
     * Where a take's bytes are, on this origin, streamed with `Range` behind
     * the session (`GET /:id/takes/:takeId[/thumb|poster|web]`). A derivative
     * that has not arrived is answered with the master, so a URL built here
     * never 404s for a take the reader may see - but the reads already list
     * the derivatives that exist (`take.files`), and the media components
     * prefer those.
     */
    takeUrl: (id: number, takeId: number, kind?: StoryboardTakeFileKind) =>
      `/api/pipeline/storyboards/${id}/takes/${takeId}${kind ? `/${kind}` : ""}`,

    /**
     * A take's URLs as the media components want them: the master, and each
     * derivative that has arrived (`null` when it has not, so a caller can
     * tell "no thumb" from "the thumb is the master"). Read from the take
     * itself: a portal read already carries same-origin paths.
     */
    takeMedia: (take: Pick<StoryboardTake, "url" | "files">): TakeMediaUrls => ({
      master: take.url,
      thumb: take.files.thumb ?? null,
      poster: take.files.poster ?? null,
      web: take.files.web ?? null,
    }),
  },

  /**
   * AI generation (`docs/ai-generation/02-contract.md` §2): runs and their
   * outputs, galleries, media, uploads, putting work on the storyboard, the
   * team's voices and product library, and the budget read before a click.
   *
   * A Generate click is a **run** with one **output** per provider call; an
   * output is not a take until a person adopts it, so nothing here but the
   * adopt family writes a storyboard revision. Those (`adopt`, `makeMain`,
   * `removeOption`, `adoptVoice`, `adoptVoiceoverA`, `adoptVoiceoverB`) are
   * storyboard writes like any other: they carry `baseRevision` and answer
   * `{ revision, changed }`, and the hooks in `use-generation.ts` send them
   * through the storyboard's write queue.
   *
   * Editors read none of it (404). Spending answers 503
   * `generation_disabled`, 402 `cap_reached` or 409 `too_many_running`; the
   * Generate button reads `budget` first so it is disabled ahead of time.
   */
  generation: {
    /** One Generate click (or an upload run, with `upload`). The idempotency key is a uuid minted per click. */
    create: (id: number, body: GenerationCreateInput) =>
      request<GenerationCreateResult>(`/api/pipeline/storyboards/${id}/generations`, { method: "POST", body: JSON.stringify(body) }),

    /** The one read the web polls: runs with their outputs; `active` = running, plus finished since `since`. */
    list: (id: number, params: GenerationListParams = {}) =>
      request<GenerationListResult>(`/api/pipeline/storyboards/${id}/generations${queryOf(params)}`),

    /** Per shot or line and kind, finished outputs of the last seven days by anyone, not a take and not hidden: the "3 new" badges. */
    unadopted: (id: number) =>
      request<GenerationUnadoptedResult>(`/api/pipeline/storyboards/${id}/generations?unadopted=7d`),

    /** Cancel what can still be cancelled; anything already rendering keeps going (409 `already_rendering`). */
    cancel: (id: number, runId: number) =>
      request<GenerationCancelResult>(`/api/pipeline/storyboards/${id}/generations/${runId}/cancel`, { method: "POST" }),

    /** "Retry this one": a new output from the run's own request, priced for one. */
    retry: (id: number, runId: number, n: number, body: GenerationRetryInput) =>
      request<GenerationCreateResult>(`/api/pipeline/storyboards/${id}/generations/${runId}/outputs/${n}/retry`, {
        method: "POST",
        body: JSON.stringify(body),
      }),

    /** Voiceover A, stitched again from each line's current take (free). Adopting it is `adoptVoiceoverA`. */
    rebuildVoiceoverA: (id: number, body: VoiceoverRebuildInput) =>
      request<GenerationCreateResult>(`/api/pipeline/storyboards/${id}/voiceover-a/rebuild`, { method: "POST", body: JSON.stringify(body) }),

    /** The team's month against its cap, and whether generating is on at all. */
    budget: () => request<GenerationBudget>("/api/pipeline/generation/budget"),

    /** The composer settings this person last chose, on their account (any device). */
    prefs: () => request<{ prefs: GenerationPrefs }>("/api/pipeline/generation/prefs"),
    /** Merged over what is kept, section by section. */
    savePrefs: (body: GenerationPrefs) => request<{ prefs: GenerationPrefs }>("/api/pipeline/generation/prefs", { method: "PATCH", body: JSON.stringify(body) }),

    /** Clip models and resolutions Higgsfield is rendering slower than usual right now (from our own recent clips). */
    clipSpeed: () =>
      request<{ lagging: Array<{ model: string; resolution: string; typicalMinutes: number; slowClips: number; recentClips: number }>; checkedAt: string }>(
        "/api/pipeline/generation/clip-speed",
      ),

    /* ---- galleries */

    /** A shot's frame (or clip) gallery: its runs, plus every take of its slots of that kind, archived ones included. */
    shotGallery: (id: number, shotKey: string, kind: "frame" | "clip", params: { hidden?: boolean } = {}) =>
      request<GalleryResult>(
        `/api/pipeline/storyboards/${id}/shots/${encodeURIComponent(shotKey)}/gallery${queryOf({ kind, hidden: params.hidden || undefined })}`,
      ),

    /** A line's voiceover gallery, with the editor's own audio for "Use as the voiceover". */
    lineGallery: (id: number, lineKey: string, params: { hidden?: boolean } = {}) =>
      request<LineGalleryResult>(
        `/api/pipeline/storyboards/${id}/lines/${encodeURIComponent(lineKey)}/gallery${queryOf({ hidden: params.hidden || undefined })}`,
      ),

    /** Voiceover B's: the whole-script reads and the `voiceover/B` slot's takes. */
    voiceoverBGallery: (id: number, params: { hidden?: boolean } = {}) =>
      request<GalleryResult>(`/api/pipeline/storyboards/${id}/voiceover-b/gallery${queryOf({ hidden: params.hidden || undefined })}`),

    /** Hide a picture (by its bytes) from one gallery; nothing is deleted. Refused for bytes on show (409 `hide_current`). */
    hide: (id: number, body: GalleryHideInput) =>
      request<GalleryHideResult>(`/api/pipeline/storyboards/${id}/gallery/hides`, { method: "POST", body: JSON.stringify(body) }),

    unhide: (id: number, body: GalleryHideInput) =>
      request<GalleryHideResult>(`/api/pipeline/storyboards/${id}/gallery/hides`, { method: "DELETE", body: JSON.stringify(body) }),

    /** The reference picker's "This presentation": non-product `ref_image` slots, and the references uploaded here. */
    references: (id: number) => request<StoryboardReferencesResult>(`/api/pipeline/storyboards/${id}/references`),

    /** The reference picker's Frames tab: every live shot's frames in reading order, as a prompt sends them. */
    frames: (id: number, params: { mine?: boolean } = {}) =>
      request<ConceptFramesResult>(`/api/pipeline/storyboards/${id}/frames${queryOf({ mine: params.mine || undefined })}`),

    /* ---- media: same origin, `Range`, behind the session; strategists and admins only */

    /** An output's bytes, or a derivative of them (`thumb`, `poster`, `web`). */
    outputUrl: (id: number, outputId: number, kind?: "thumb" | "poster" | "web") =>
      `/api/pipeline/storyboards/${id}/outputs/${outputId}${kind ? `/${kind}` : ""}`,

    /** A voiceover output's waveform, precomputed by the worker. */
    peaks: (id: number, outputId: number) =>
      request<{ peaks: unknown }>(`/api/pipeline/storyboards/${id}/outputs/${outputId}/peaks`),

    referenceUploadUrl: (id: number, sha256: string, thumb = false) =>
      `/api/pipeline/storyboards/${id}/reference-uploads/${sha256}${thumb ? "/thumb" : ""}`,

    /**
     * Raw bytes into the storyboard's blob space, by their sha256 (the
     * browser hashes them first). `reference` also lists them under "Uploaded
     * here"; a frame, clip or audio then becomes an upload run through
     * `create` with `upload`. The content type travels as the header, so it
     * replaces the JSON default.
     */
    upload: (id: number, sha256: string, file: Blob, meta: { kind: GenerationUploadKind; name?: string; width?: number; height?: number }) =>
      request<GenerationUploadResult>(`/api/pipeline/storyboards/${id}/uploads/${sha256}${queryOf(meta)}`, {
        method: "PUT",
        headers: { "content-type": file.type || "application/octet-stream" },
        body: file,
      }),

    /* ---- putting work on the storyboard: one portal revision each, with `baseRevision` */

    /** An output or an older take onto an option (`{ slotId }`), as a new option (`"new"`), or into the shot's clip (`"clip"`). */
    adopt: (id: number, shotKey: string, body: StoryboardWrite<GenerationAdoptInput>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/shots/${encodeURIComponent(shotKey)}/adopt`, "POST", body),

    /** "Make it the shot": The shot and that option swap pictures. */
    makeMain: (id: number, shotKey: string, body: StoryboardWrite<GenerationOptionInput>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/shots/${encodeURIComponent(shotKey)}/make-main`, "POST", body),

    /** Remove an option, The shot included (the next option becomes it). The last one is refused (`last_option`). */
    removeOption: (id: number, shotKey: string, body: StoryboardWrite<GenerationOptionInput>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/shots/${encodeURIComponent(shotKey)}/remove-option`, "POST", body),

    /** A take (or an upload, or the editor's file) as the line's voiceover, in that voice's slot. */
    adoptVoice: (id: number, lineKey: string, body: StoryboardWrite<GenerationAdoptVoiceInput>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/lines/${encodeURIComponent(lineKey)}/adopt-voice`, "POST", body),

    /** A voiceover output or an earlier take of the same slot, on show in one voiceover slot (a free clip's, or a line's own). */
    adoptToSlot: (id: number, slotId: number, body: StoryboardWrite<GenerationAdoptToSlotInput>) =>
      write<StoryboardEditResult & { slotId: number; takeId: number }>(`/api/pipeline/storyboards/${id}/voice-slots/${slotId}/adopt`, "POST", body),

    /** A rebuilt Voiceover A; refused (`stale_stitch`) once a line's voice moved since its Rebuild. */
    adoptVoiceoverA: (id: number, body: StoryboardWrite<VoiceoverAAdoptInput>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/voiceover-a/adopt`, "POST", body),

    adoptVoiceoverB: (id: number, body: StoryboardWrite<VoiceoverBAdoptInput>) =>
      write<StoryboardEditResult>(`/api/pipeline/storyboards/${id}/voiceover-b/adopt`, "POST", body),

    /**
     * Re-voice (docs/presentation-editor/revoice): a whole voiceover read
     * again in another voice. `create` queues one run per line (and one for
     * B) in one go; the batch is read back with each line's state; `apply`
     * and `revert` are storyboard writes (one revision each, `baseRevision`).
     */
    revoice: {
      list: (id: number) => request<RevoiceListResult>(`/api/pipeline/storyboards/${id}/revoice`),
      get: (id: number, batchId: number) => request<RevoiceBatchView>(`/api/pipeline/storyboards/${id}/revoice/${batchId}`),
      create: (id: number, body: RevoiceCreateInput) =>
        request<RevoiceCreateResult>(`/api/pipeline/storyboards/${id}/revoice`, { method: "POST", body: JSON.stringify(body) }),
      cancel: (id: number, batchId: number) => request<RevoiceBatchView>(`/api/pipeline/storyboards/${id}/revoice/${batchId}/cancel`, { method: "POST" }),
      retry: (id: number, batchId: number, body: RevoiceRetryInput) =>
        request<RevoiceBatchView>(`/api/pipeline/storyboards/${id}/revoice/${batchId}/retry`, { method: "POST", body: JSON.stringify(body) }),
      discard: (id: number, batchId: number) => request<RevoiceBatchView>(`/api/pipeline/storyboards/${id}/revoice/${batchId}/discard`, { method: "POST" }),
      apply: (id: number, batchId: number, body: RevoiceApplyInput) => write<RevoiceApplyResult>(`/api/pipeline/storyboards/${id}/revoice/${batchId}/apply`, "POST", body),
      revert: (id: number, batchId: number, body: RevoiceApplyInput) => write<RevoiceApplyResult>(`/api/pipeline/storyboards/${id}/revoice/${batchId}/revert`, "POST", body),
    },

    /* ---- the team's voices */

    voices: {
      /** `recheck` re-reads the list at ElevenLabs when it is older than a day. */
      list: (params: { archived?: boolean; recheck?: boolean } = {}) =>
        request<{ voices: GenerationVoice[] }>(`/api/pipeline/generation/voices${queryOf(params)}`),

      /**
       * Add a voice by its ElevenLabs id, checked live against our account;
       * nothing is saved unless it is found. A refusal's code is the check's
       * answer (`not_in_account`, `not_a_voice_id`, `key_problem`,
       * `elevenlabs_unavailable`, `duplicate` with the listed voice in
       * `details.voice`).
       */
      /**
       * A storyboard's own voice alias (an agent's) as the team's voice: added
       * to the list when the storyboard's takes name a real ElevenLabs voice;
       * `voice` null when it cannot be.
       */
      resolve: (sb: number, body: { alias: string; line?: string }) =>
        request<VoiceResolveResult>(`/api/pipeline/storyboards/${sb}/voices/resolve`, { method: "POST", body: JSON.stringify(body) }),

      add: (body: VoiceAddInput) =>
        request<VoiceAddResult>("/api/pipeline/generation/voices", { method: "POST", body: JSON.stringify(body) }),

      /** Rename, re-note, archive or bring back. `ifUpdatedAt` guards an undo (409 `undo_conflict`). */
      patch: (voiceId: number, body: VoicePatchInput) =>
        request<{ voice: GenerationVoice }>(`/api/pipeline/generation/voices/${voiceId}`, { method: "PATCH", body: JSON.stringify(body) }),

      /** The voice's sample, served from our own copy on this origin. */
      previewUrl: (voiceId: number) => `/api/pipeline/generation/voices/${voiceId}/preview`,

      /** Read the voice's ElevenLabs name, labels and sample again. */
      refresh: (voiceId: number) => request<{ voice: GenerationVoice }>(`/api/pipeline/generation/voices/${voiceId}/refresh`, { method: "POST" }),

      /** What ElevenLabs says about an id, saving nothing (the add dialog's preview). */
      lookup: (elevenId: string) => request<VoiceLookupResult>(`/api/pipeline/generation/voices/lookup/${encodeURIComponent(elevenId)}`),

      /** An unsaved id's sample, on this origin. */
      lookupPreviewUrl: (elevenId: string) => `/api/pipeline/generation/voices/lookup/${encodeURIComponent(elevenId)}/preview`,
    },

    /* ---- the product library */

    library: {
      list: (params: { archived?: boolean } = {}) =>
        // `sop`: the admin's SOP sets, when the server shares them with strategists (see `useProductSop`).
        request<{ images: LibraryImage[]; sop?: ProductSop }>(`/api/pipeline/generation/library${queryOf(params)}`),

      /** A new image's bytes, into the library's own storage, before `add` describes them. */
      uploadBlob: (sha256: string, file: Blob) =>
        request<GenerationUploadResult>(`/api/pipeline/generation/library/blobs/${sha256}`, {
          method: "PUT",
          headers: { "content-type": file.type || "application/octet-stream" },
          body: file,
        }),

      /** Always a new row. Every answer carries `assetMapLine`: what the AI is told. */
      add: (body: LibraryAddInput) =>
        request<{ image: LibraryImage }>("/api/pipeline/generation/library", { method: "POST", body: JSON.stringify(body) }),

      patch: (imageId: number, body: LibraryPatchInput) =>
        request<{ image: LibraryImage }>(`/api/pipeline/generation/library/${imageId}`, { method: "PATCH", body: JSON.stringify(body) }),

      imageUrl: (imageId: number, thumb = false) => `/api/pipeline/generation/library/${imageId}/image${thumb ? "/thumb" : ""}`,
    },
  },

  /** The team's working links. Everyone reads the same list; admins keep it. */
  resources: {
    list: () => request<ResourcesResponse>("/api/resources"),

    create: (body: ResourceCreateRequest) =>
      request<Resource>("/api/resources", { method: "POST", body: JSON.stringify(body) }),

    update: (id: number, patch: ResourcePatchRequest) =>
      request<Resource>(`/api/resources/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),

    remove: (id: number) => request<void>(`/api/resources/${id}`, { method: "DELETE" }),
  },
};

/** GET /storyboards/:id/generations's filters (not `unadopted`, which is its own read). */
export interface GenerationListParams {
  /** A shot's or a line's key. */
  owner?: string;
  /** Only the signed-in person's runs. */
  mine?: boolean;
  /** Running, plus finished since `since`. */
  active?: boolean;
  /** ISO time. */
  since?: string;
}

/** GET /api/admin/generation/spend's period: Europe/Rome dates, bucketed by day, week or month. */
export interface SpendParams {
  from?: string;
  to?: string;
  bucket?: "day" | "week" | "month";
}

/** GET /api/admin/generation/spend/outputs's filters. */
export interface SpendFeedParams {
  from?: string;
  to?: string;
  /** A user id. */
  person?: number;
  model?: string;
  kind?: string;
  status?: string;
  cursor?: string;
}

/** GET /api/admin/activity/feed's filters. Mirrors the server's schema. */
export interface ActivityFeedParams {
  user?: number;
  kinds?: readonly ActivityKind[];
  cursor?: number;
  limit?: number;
  /** ISO instant: only entries at or after it. */
  since?: string;
}

/** The Activity page's period: local days in a zone, and an optional person. */
export interface ActivityPeriodParams {
  from: string;
  to: string;
  tz: string;
  user?: number;
}

function periodQuery(params: ActivityPeriodParams): string {
  const query = new URLSearchParams({ from: params.from, to: params.to, tz: params.tz });
  if (params.user !== undefined) query.set("user", String(params.user));
  return query.toString();
}

/** Filters the brief list and the board both take. Mirrors the server's schema. */
export interface BriefListParams {
  /** `mine` is the role's own lens; `all` is the deliberate ask for everything. */
  scope?: "mine" | "all";
  /** One stage or several - the board asks for a column at a time. */
  stage?: PipelineStage | PipelineStage[];
  lane?: PipelineLane;
  editor?: number;
  strategist?: number;
  product?: string;
  /** `YYYY-MM-DD`: due on or before that day. */
  due?: string;
  q?: string;
  /** Only briefs still moving: everything but shipped and cancelled. */
  live?: boolean;
  sort?: "recent" | "due" | "waiting";
  limit?: number;
  cursor?: number;
}

/* -------------------------------------------------------------- storyboards */

/** GET /api/pipeline/storyboards's filters. */
export interface StoryboardListParams {
  /** A concept's ref, `CPT-3993`. */
  brief?: string;
  /** A hosted deck's slug. */
  presentation?: string;
  archived?: boolean;
}

/** GET /api/pipeline/storyboards/:id's switches. */
export interface StoryboardReadParams {
  /** The editors' snapshot rather than the live draft. */
  published?: boolean;
  /** Every take of every slot, not only the one on show. */
  history?: boolean;
  /** Archived lines, shots and slots too. */
  archived?: boolean;
  /** Leave the prompts out (`promptsOmitted`); `api.storyboards.prompts` answers them. */
  lean?: boolean;
}

/** GET /api/pipeline/storyboards/:id/revisions's filters. */
export interface StoryboardRevisionParams {
  entity?: string;
  /** `l03`, `s07`; a shot's key matches its slots too. */
  key?: string;
  limit?: number;
  /** The previous page's `nextBefore`. */
  before?: number;
}

/**
 * A storyboard write: the change, plus `baseRevision` (the revision it was
 * built from) and an optional `note` kept on the revision row.
 */
export type StoryboardWrite<T extends object> = T & StoryboardPatchMeta;

/** PATCH /api/pipeline/storyboards/:id, as the portal's schema takes it. */
export interface StoryboardPatchRequest {
  title?: string;
  brand?: string | null;
  /** Merged one top-level key at a time: a key sent is set, a key sent as `null` is removed. */
  meta?: Record<string, unknown>;
  briefRef?: string | null;
  archived?: boolean;
  /** Images and video, or images only; see `StoryboardMediaModePatch`. */
  mediaMode?: StoryboardMediaMode;
  /** With `mediaMode: "images"`: the person saw the clips it hides (`409 clips_present` otherwise). */
  hideClips?: boolean;
  /** What the ad sells, written on the concept it briefs; see `StoryboardProductPatch`. */
  product?: ConceptProduct;
}

/**
 * PATCH .../lines/:key from the portal: the agent's line patch without the
 * two fields only the agent may change (`kind`, `variantOf`), with the note.
 */
export type StoryboardLinePatchRequest = Pick<
  StoryboardLinePatch,
  "text" | "speaker" | "durationMs" | "meta" | "noteMd"
> & {
  /** `voiceover | dialogue | on_screen | none`; anything else is refused. */
  delivery?: StoryboardLineDelivery | null;
};

/** PATCH .../shots/:key from the portal. */
export type StoryboardShotPatchRequest = Pick<
  StoryboardShotPatch,
  "line" | "description" | "action" | "onScreenText" | "cutToMs" | "durationMs" | "meta"
>;

/** PATCH .../slots/:slotId from the portal (a slot's `meta` is the agent's). */
export type StoryboardSlotPatchRequest = Pick<StoryboardSlotPatch, "label" | "prompt" | "params" | "currentTakeId">;

/** A take's same-origin URLs. `null` for a derivative that has not arrived. */
export interface TakeMediaUrls {
  /** The master; null while the take is pending or failed. */
  master: string | null;
  /** A 540-wide JPEG of a frame or still. */
  thumb: string | null;
  /** A JPEG of a clip's first frame. */
  poster: string | null;
  /** A 720p H.264 mp4 of a clip (480p for the reference video). */
  web: string | null;
}

/**
 * How a write leaves, beyond its body.
 *
 * `keepalive`: the request is finished even if the page is closed or
 * reloaded while it is on its way (`fetch`'s own flag; the browser caps such
 * bodies at 64 KB, far above a line's words). For a save made as the page
 * goes away, when nothing would be left to send it otherwise.
 */
export interface WriteOptions {
  keepalive?: boolean;
}

/** A JSON write: every storyboard edit is one, including the deletes, whose body carries `baseRevision`. */
function write<T>(path: string, method: "POST" | "PATCH" | "DELETE", body: object, options: WriteOptions = {}): Promise<T> {
  return request<T>(path, {
    method,
    body: JSON.stringify(body),
    ...(options.keepalive ? { keepalive: true } : {}),
  });
}

/**
 * `?a=1&b=2`, or nothing at all.
 *
 * Undefined keys are dropped rather than sent empty: the pipeline's query
 * schema is strict and refuses `editor=`, which is the right call on the server
 * and would be a 400 for a filter nobody set.
 */
function queryOf<T extends object>(params: T): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, unknown][]) {
    if (value === undefined || value === null || value === "") continue;
    if (typeof value === "boolean") search.set(key, value ? "1" : "0");
    else if (Array.isArray(value)) {
      if (value.length > 0) search.set(key, value.join(","));
    } else search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export type { Role };
